import { useContext, useState } from "react"
import { useTranslation } from "react-i18next"
import { noop } from "lodash-es"
import { PayrollChangeInfo } from "../PayrollChangeInfo"
import { AddPayrollChangeForm } from "./components/AddPayrollChangeForm"
import { PayrollContext } from "../../contexts/PayrollContext/PayrollContext"
import { Modal, Button, Alert } from "@oysterhr/ui"
import { Authorized, useAuthorized } from "@oysterhr/auth"
import { PayrollChangeRestrictedModal } from "./components/PayrollChangeRestrictedModal"
import { FormProvider, useForm } from "react-hook-form"
import { PayrollChangeFormValues } from "./components/AddPayrollChangeForm/types"
import { yupResolver } from "@hookform/resolvers/yup"
import { getPayrollChangeValidationSchema } from "./components/AddPayrollChangeForm/validations"
import { useCompanyEngagementTypes } from "../../hooks/useCompanyEngagementTypes"
import { LoadingModal } from "./components/LoadingModal"
import { useFlags } from "@oysterhr/feature-flags"

interface Props {
  engagementId?: string
  isTeamMemberAndDateReadonly?: boolean
  onChange?: () => void
  close: () => void
  period?: string
}

export const PayrollChangePopup = ({
  engagementId,
  period,
  isTeamMemberAndDateReadonly = false,
  onChange = noop,
  close,
}: Props) => {
  const { t } = useTranslation("Payroll")
  const payrollContext = useContext(PayrollContext)
  const { companyId, authorizations, company } = payrollContext
  const form = useForm<PayrollChangeFormValues>({
    resolver: yupResolver(getPayrollChangeValidationSchema(t)),
    mode: "onChange",
    defaultValues: {
      month: period,
    },
  })
  const { pcBadDebtManagement } = useFlags()
  const [isFTE, setIsFTE] = useState(false)
  const { isAuthorized } = useAuthorized({
    authorizations: company?.paymentsSettings?.authorizations,
  })
  const { loading, hasOnlyFTEs } = useCompanyEngagementTypes({
    variables: { companyId },
    skip: !pcBadDebtManagement || !company?.paymentsSettings?.restricted,
  })

  if (pcBadDebtManagement && company?.paymentsSettings?.restricted && loading) {
    return <LoadingModal close={close} />
  }

  const isRestricted = pcBadDebtManagement && company?.paymentsSettings?.restricted
  const isRestrictedButAllowed = isRestricted && isAuthorized(["update_restriction"])
  const isRestrictedAndNotAllowed = isRestricted && !isAuthorized(["update_restriction"])

  if (isRestrictedAndNotAllowed && hasOnlyFTEs) {
    return <PayrollChangeRestrictedModal onClose={close} />
  }

  return (
    <FormProvider {...form}>
      <Modal isOpen onRequestClose={close}>
        <Modal.Header>{t("modal.title")}</Modal.Header>
        <Modal.Content>
          <Authorized to={["add_payroll_change"]} on={{ authorizations }}>
            <PayrollChangeInfo
              className="mb-4"
              companyId={companyId}
              payrollContext={payrollContext}
            />
            <AddPayrollChangeForm
              externalEngagementId={engagementId}
              isDateReadOnly={isTeamMemberAndDateReadonly}
              isTeamMemberReadonly={isTeamMemberAndDateReadonly}
              onSuccess={onChange}
              close={close}
              setIsFTE={setIsFTE}
            />
          </Authorized>
        </Modal.Content>
        <Modal.Footer className="flex flex-col gap-4">
          {isRestrictedButAllowed && isFTE && (
            <Alert variation="warning">
              <Alert.Icon />
              <Alert.Content>{t("restrictedButAllowed.alert.content")}</Alert.Content>
            </Alert>
          )}
          <div className="flex w-full justify-end gap-4">
            <Button variation="outline" type="button" onClick={close}>
              {t("buttons.cancel")}
            </Button>
            <Button
              variation="primary"
              type="submit"
              form="add-payroll-change-form"
              disabled={!form.formState.isValid || form.formState.isSubmitting}
              data-analytics-name="add-payroll-change-modal_submit"
            >
              {t("buttons.submit")}
            </Button>
          </div>
        </Modal.Footer>
      </Modal>
    </FormProvider>
  )
}
